import { useEffect, useState, useMemo, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { cbotUsdT, miles } from "./lib";
import CbotStrip from "./CbotStrip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus, Trash2, Target } from "lucide-react";
import { toast } from "sonner";

const STATE = { LEJOS: "border-slate-200", CERCA: "border-amber-400", ALCANZADO: "border-[#14532d]" };
const BADGE = { LEJOS: "bg-slate-400", CERCA: "bg-amber-600", ALCANZADO: "bg-[#14532d]" };

export default function GranosPrecioDeseado() {
  const [rows, setRows] = useState([]);
  const [raw, setRaw] = useState([]);
  const [cbot, setCbot] = useState(null);
  const [margen, setMargen] = useState(2);
  const [mode, setMode] = useState("margen");
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [edit, setEdit] = useState({});
  const [editC, setEditC] = useState({});

  const loadCbot = useCallback(() => api.get("/granos/cbot").then((r) => setCbot(r.data)).catch(() => {}), []);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, c, s] = await Promise.all([
        api.get("/granos/precio-deseado"),
        api.get("/granos/contratos"),
        api.get("/granos/notif-settings").catch(() => ({ data: {} })),
      ]);
      setRows(d.data.items || []);
      setRaw(c.data.contratos || []);
      if (s?.data?.margen_pct != null) setMargen(s.data.margen_pct);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    loadCbot();
    const id = setInterval(loadCbot, 60000);
    return () => clearInterval(id);
  }, [load, loadCbot]);

  const eff = mode === "alcance" ? 0 : margen;
  const computed = useMemo(() => {
    const items = cbot?.items || [];
    const arr = rows.map((d) => {
      const abiertas = raw.filter((x) => (x.productor || x.cliente) === d.cliente && (x.cultivo || x.producto) === d.cultivo)
        .reduce((s, x) => s + (Number(x.pendiente) || 0), 0);
      const chi = cbotUsdT(d.cultivo, items);
      const costo = +d.costo_entrega_usd_tn || 0;
      const dp = (+d.precio_usd_tn || 0) - costo;
      let est = "LEJOS", pct = 0, falta = null, superado = null;
      if (chi != null && dp) {
        pct = Math.min(100, (chi / dp) * 100);
        if (chi >= dp) { est = "ALCANZADO"; superado = chi - dp; }
        else if (chi >= dp * (1 - eff / 100)) { est = "CERCA"; falta = dp - chi; }
        else { est = "LEJOS"; falta = dp - chi; }
      }
      return { ...d, abiertas, chi, dp, costo, est, pct, falta, superado };
    });
    const rank = { ALCANZADO: 0, CERCA: 1, LEJOS: 2 };
    return arr.sort((a, b) => rank[a.est] - rank[b.est] || b.abiertas - a.abiertas);
  }, [rows, raw, cbot, eff]);

  const savePrice = async (cliente, cultivo, precio, costo) => {
    const p = parseFloat(String(precio).replace(",", "."));
    if (isNaN(p)) return;
    const c = parseFloat(String(costo ?? 0).replace(",", "."));
    try {
      await api.post("/granos/precio-deseado", { cliente, cultivo, precio_usd_tn: p, costo_entrega_usd_tn: isNaN(c) ? 0 : c });
      setEdit((e) => ({ ...e, [`${cliente}|${cultivo}`]: undefined }));
      setEditC((e) => ({ ...e, [`${cliente}|${cultivo}`]: undefined }));
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar"); }
  };
  const saveMargen = async (v) => {
    if (isNaN(v)) return;
    setMargen(v); setMode("margen");
    try { await api.put("/granos/notif-settings", { margen_pct: v }); } catch { /* ignore */ }
  };
  const del = async (cliente, cultivo) => {
    if (!window.confirm(`¿Quitar el precio deseado de ${cliente} (${cultivo})?`)) return;
    try { await api.delete("/granos/precio-deseado", { params: { cliente, cultivo } }); load(); } catch { /* ignore */ }
  };

  return (
    <div className="p-4 space-y-4" data-testid="granos-precio-deseado-tab">
      <CbotStrip />

      <div className="rounded-2xl border bg-white p-4 space-y-2">
        <p className="font-extrabold text-slate-900 text-sm">Avisarme cuando Chicago</p>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => setMode("alcance")} data-testid="granos-pd-mode-alcance"
            className={`rounded-lg py-2 text-xs font-bold border ${mode === "alcance" ? "bg-[#14532d] text-white border-[#14532d]" : "bg-white text-slate-700 border-slate-200"}`}>
            Alcance el precio
          </button>
          <div className={`rounded-lg border flex items-center px-1 ${mode === "margen" ? "bg-[#14532d] text-white border-[#14532d]" : "bg-white text-slate-700 border-slate-200"}`}>
            <button onClick={() => setMode("margen")} className="flex-1 py-2 text-xs font-bold">Esté a</button>
            <input type="number" value={margen} onChange={(e) => setMargen(Number(e.target.value))} onBlur={(e) => saveMargen(Number(e.target.value))}
              className="w-10 bg-transparent text-center text-xs font-bold outline-none" data-testid="granos-pd-margen" />
            <span className="pr-1 text-xs font-bold">% o menos</span>
          </div>
        </div>
        <p className="text-[11px] text-slate-400">Notificación push, una vez por día por cliente.</p>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0f5233]" /></div>
      ) : (
        <div className="space-y-2.5">
          {computed.map((r) => {
            const key = `${r.cliente}|${r.cultivo}`;
            return (
              <div key={key} className={`rounded-2xl border-2 ${STATE[r.est]} bg-white p-3 space-y-2`} data-testid="granos-pd-card">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-extrabold text-slate-900 text-sm truncate">{r.cliente}</p>
                    <p className="text-[11px] text-slate-500">{r.cultivo} · {miles(r.abiertas)} t abiertas</p>
                  </div>
                  <span className={`shrink-0 rounded-md ${BADGE[r.est]} text-white text-[10px] font-extrabold px-2 py-1`}>{r.est}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[11px] text-slate-500">Precio deseado (USD/t)</Label>
                    <Input inputMode="decimal" defaultValue={String(r.precio_usd_tn)} className="h-9 text-right font-bold"
                      onChange={(e) => setEdit((x) => ({ ...x, [key]: e.target.value }))}
                      onBlur={() => savePrice(r.cliente, r.cultivo, edit[key] ?? r.precio_usd_tn, editC[key] ?? r.costo_entrega_usd_tn)}
                      data-testid="granos-pd-price" />
                  </div>
                  <div>
                    <Label className="text-[11px] text-slate-500">Costo de entrega (USD/t)</Label>
                    <Input inputMode="decimal" defaultValue={String(r.costo_entrega_usd_tn || 0)} className="h-9 text-right font-bold"
                      onChange={(e) => setEditC((x) => ({ ...x, [key]: e.target.value }))}
                      onBlur={() => savePrice(r.cliente, r.cultivo, edit[key] ?? r.precio_usd_tn, editC[key] ?? r.costo_entrega_usd_tn)}
                      data-testid="granos-pd-costo" />
                  </div>
                </div>
                <div className="flex items-center justify-between rounded-lg bg-[#f1f4f0] px-3 py-1.5">
                  <span className="text-[11px] font-semibold text-slate-500">Precio final (deseado − costo)</span>
                  <span className="text-sm font-extrabold text-[#14532d]" data-testid="granos-pd-final">USD {Math.round(r.dp)}</span>
                </div>
                <div className="h-2 rounded bg-slate-100 overflow-hidden"><div className={`h-2 ${BADGE[r.est]}`} style={{ width: `${Math.max(0, Math.min(100, r.pct))}%` }} /></div>
                <div className="flex items-center justify-between">
                  <p className="text-[11px] text-slate-500">
                    {r.chi == null ? "Chicago: —" :
                      r.est === "ALCANZADO" ? `Chicago hoy USD ${Math.round(r.chi)} · superado por USD ${Math.round(r.superado)}` :
                        `Chicago hoy USD ${Math.round(r.chi)} · faltan USD ${Math.round(r.falta)}`}
                  </p>
                  <button onClick={() => del(r.cliente, r.cultivo)} className="text-red-500 p-1" data-testid="granos-pd-del"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            );
          })}
          <button onClick={() => setAddOpen(true)} data-testid="granos-pd-add"
            className="w-full rounded-2xl border-2 border-dashed border-[#14532d] py-3 text-sm font-extrabold text-[#14532d] flex items-center justify-center gap-2">
            <Plus className="h-4 w-4" /> Agregar precio deseado
          </button>
        </div>
      )}

      <AddDialog open={addOpen} onClose={() => setAddOpen(false)} raw={raw} onSave={savePrice} />
    </div>
  );
}

function AddDialog({ open, onClose, raw, onSave }) {
  const [cliente, setCliente] = useState("");
  const [cultivo, setCultivo] = useState("");
  const [precio, setPrecio] = useState("");
  const [costo, setCosto] = useState("");
  useEffect(() => { if (open) { setCliente(""); setCultivo(""); setPrecio(""); setCosto(""); } }, [open]);
  const clientes = useMemo(() => Array.from(new Set(raw.map((c) => c.productor || c.cliente).filter(Boolean))).sort(), [raw]);
  const cultivos = useMemo(() => Array.from(new Set(raw.map((c) => c.cultivo || c.producto).filter(Boolean))).sort(), [raw]);
  const pf = (parseFloat(precio.replace(",", ".")) || 0) - (parseFloat(costo.replace(",", ".")) || 0);
  const submit = async () => {
    const p = parseFloat(precio.replace(",", "."));
    if (!cliente || !cultivo || isNaN(p)) { toast.error("Completá cliente, cultivo y precio."); return; }
    await onSave(cliente, cultivo, p, costo); onClose();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="granos-pd-add-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Target className="h-5 w-5 text-[#0f5233]" /> Agregar precio deseado</DialogTitle><DialogDescription>Elegí cliente, cultivo y el precio objetivo en USD por tonelada.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Cliente</Label>
            <Select value={cliente} onValueChange={setCliente}>
              <SelectTrigger className="h-11" data-testid="granos-pd-add-cliente"><SelectValue placeholder="Elegir cliente" /></SelectTrigger>
              <SelectContent>{clientes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Cultivo</Label>
            <Select value={cultivo} onValueChange={setCultivo}>
              <SelectTrigger className="h-11" data-testid="granos-pd-add-cultivo"><SelectValue placeholder="Elegir cultivo" /></SelectTrigger>
              <SelectContent>{cultivos.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Precio deseado (USD/t)</Label>
            <Input inputMode="decimal" value={precio} onChange={(e) => setPrecio(e.target.value)} placeholder="Ej: 450" className="h-11" data-testid="granos-pd-add-precio" />
          </div>
          <div className="space-y-1">
            <Label>Costo de entrega (USD/t)</Label>
            <Input inputMode="decimal" value={costo} onChange={(e) => setCosto(e.target.value)} placeholder="Ej: 25" className="h-11" data-testid="granos-pd-add-costo" />
          </div>
          <div className="flex items-center justify-between rounded-lg bg-[#f1f4f0] px-3 py-2">
            <span className="text-xs font-semibold text-slate-500">Precio final (deseado − costo)</span>
            <span className="text-base font-extrabold text-[#14532d]">USD {Math.round(pf)}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} className="bg-[#0f5233] hover:bg-[#0b3f27]" data-testid="granos-pd-add-save">Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
